import { useState, ReactNode } from 'react';
import { confirmMonth } from '../api/adminApi';
import styles from './ConfirmMonthChecklist.module.css';

interface MissingMonth {
  year: number;
  month: number;
  monthName: { ar: string; en: string };
}

interface Props {
  locationCode: string;
  missingMonths: MissingMonth[];
  hint: ReactNode;
  onConfirmed: () => void;
}

/**
 * Interactive checklist of months that must be confirmed before a later target
 * month can be confirmed, per the invariant that months are confirmed in unbroken
 * sequence (see backend confirmMonth / getConfirmMonthPreview). Each item confirms
 * independently; onConfirmed lets the caller refetch its preview afterward.
 */
export default function ConfirmMonthChecklist({ locationCode, missingMonths, hint, onConfirmed }: Props) {
  return (
    <div className={styles.checklist}>
      <p className={styles.checklistHint}>{hint}</p>
      {missingMonths.map((m) => (
        <ChecklistItem
          key={`${m.year}-${m.month}`}
          locationCode={locationCode}
          year={m.year}
          month={m.month}
          monthNameEn={m.monthName.en}
          onConfirmed={onConfirmed}
        />
      ))}
    </div>
  );
}

interface ChecklistItemProps {
  locationCode: string;
  year: number;
  month: number;
  monthNameEn: string;
  onConfirmed: () => void;
}

function ChecklistItem({ locationCode, year, month, monthNameEn, onConfirmed }: ChecklistItemProps) {
  const [startDate, setStartDate] = useState('');
  const [source, setSource] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  async function handleConfirm() {
    if (!startDate) { setError('Start date required.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await confirmMonth({
        locationCode,
        year,
        month,
        startDate,
        source: source.trim() || 'Manual confirmation',
      });
      setDone(true);
      onConfirmed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className={styles.checklistItem}>
        <div className={styles.checklistItemHeader}>
          <span className={styles.checklistItemLabel}>{monthNameEn} {year}</span>
          <span className={styles.checklistItemDone}>✓ Confirmed</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.checklistItem}>
      <div className={styles.checklistItemHeader}>
        <span className={styles.checklistItemLabel}>{monthNameEn} {year} — not yet confirmed</span>
      </div>
      <div className={styles.checklistItemForm}>
        <input
          type="date"
          className={styles.input}
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          placeholder="Start date"
        />
        <input
          type="text"
          className={styles.input}
          value={source}
          onChange={e => setSource(e.target.value)}
          placeholder="Source (optional)"
        />
        <button className={styles.checklistConfirmBtn} onClick={handleConfirm} disabled={!startDate || submitting}>
          {submitting ? 'Confirming…' : 'Confirm'}
        </button>
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}
